/**
 * The plant search, as the plant fields ask it (B121; moved word for word out of the old catalogue hook by W24 Job B).
 *
 * The plant search is the server's (B121; the owner's rule 1): usePlantSearch sends what was typed (GET /plants/search,
 * api/plantSearch) and hands back the server's rows, already named, ordered and cut, with the crops (and a bed's plants)
 * that answer, for the drop-downs to narrow to. Nothing is matched or ranked in the app: its own matcher, which listed one
 * plant twice ("Lettuce · romaine" and "Romaine · Lettuce", for about 20 crops since 10 Oct), is gone. The search's
 * request is cached under PLANT_SEARCH_QUERY_KEY, by garden, language, words and bed, for QUERY_STALE_TIME_MS.
 *
 * Words from a to-do (E18, "Plant romain lettuce") are sent as they are: the server leaves out the filler words.
 *
 * Who uses it: PlantIdentityFields, the photo tray's PileCard and Today's Plant something (usePlantSomething).
 *
 * @module usePlantSearch
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getPlantSearch, type PlantSearchAnswer } from '@/shared/api/plantSearch';
import { QUERY_STALE_TIME_MS } from '@/config/queryClient';
import { usePaused } from '@/features/what-to-plant/useWhatToPlantSearch';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { chosenGarden } from '@/shared/api/gardenMembers';

/** The first word of the plant search's React Query key (usePlantSearch); naming a variety marks it stale. @kept K1 */
export const PLANT_SEARCH_QUERY_KEY = 'plant-search';

/** What usePlantSearch hands back. */
export interface PlantSearchReading {
  /** The server's answer for the words last sent (the last answer stays while the next is on its way); undefined before any. */
  data: PlantSearchAnswer | undefined;
  /** True once the answer drawn is the one for what is in the box now: only then may "nothing found" be said. */
  current: boolean;
  /** The failure, or null. */
  error: unknown;
}

/**
 * Asks the server which rows answer what was typed (B121), a moment after typing stops; nothing is asked for an empty box.
 *
 * @param query - What is typed (trimmed here).
 * @param locationId - The bed whose growing plants come first (the photo tray); null for none.
 * @param enabled - False when there is no search box to answer.
 * @returns The answer, whether it is the one for what is typed now, and any failure.
 */
export function usePlantSearch(query: string, locationId: number | null = null, enabled = true): PlantSearchReading {
  const { mysqlUser } = useAuth();
  const { i18n: { language } } = useTranslation();
  const gardenId = chosenGarden() ?? mysqlUser?.user_id ?? null;
  const typed = query.trim();
  const sent = usePaused(typed);
  const { data, error, isFetching, isPlaceholderData } = useQuery({
    queryKey: [PLANT_SEARCH_QUERY_KEY, gardenId, language, sent, locationId],
    queryFn: () => getPlantSearch(sent, language, locationId),
    enabled: enabled && Boolean(mysqlUser) && sent.length > 0,
    staleTime: QUERY_STALE_TIME_MS,
    // While typing goes on, the last answer stays drawn until the next one is in.
    placeholderData: keepPreviousData,
  });
  return {
    data: typed ? data : undefined,
    current: typed !== '' && sent === typed && data !== undefined && !isPlaceholderData && !isFetching,
    error: typed ? error ?? null : null,
  };
}
